import {
  Favorite as HeartIcon,
  Air as AirIcon,
  DeviceThermostat as TempIcon,
  LocalHospital as BloodIcon,
  ShowChart as HRVIcon,
  DirectionsWalk as StepsIcon,
  Straighten as DistanceIcon,
  LocalFireDepartment as CaloriesIcon,
  FitnessCenter as WorkoutsIcon,
  Stairs as FloorsIcon,
  Scale as WeightIcon,
  Height as HeightIcon,
  MonitorHeart as BloodPressureIcon,
  Water as HydrationIcon,
  Restaurant as NutritionIcon,
  Female as FemaleIcon,
  Hotel as BedIcon,
  Timeline as SleepAnalysisIcon,
  Layers as SleepStagesIcon,
  EventNote as SleepEventsIcon,
  AccessTime as SleepTimeIcon,
  Timer as ActualSleepTimeIcon,
  SelfImprovement as WellBeingIcon,
} from "@mui/icons-material";

import { MetricValuesAggregationRequestDtoMetricCodeEnum } from "../libs/cellera-sdk";

export interface MetricConfig {
  name: string;
  unit: string;
  icon: React.ComponentType<{ sx?: Record<string, unknown> }>;
  category: "vital" | "activity" | "body" | "nutrition" | "sleep";
}

// Centralized metric configuration mapping
export const METRIC_CONFIGS: Record<string, MetricConfig> = {
  // Vital Signs
  [MetricValuesAggregationRequestDtoMetricCodeEnum.HeartRate]: {
    name: "Heart Rate",
    unit: "BPM",
    icon: HeartIcon,
    category: "vital",
  },
  [MetricValuesAggregationRequestDtoMetricCodeEnum.BloodPressure]: {
    name: "Blood Pressure",
    unit: "mmHg",
    icon: BloodPressureIcon,
    category: "vital",
  },
  [MetricValuesAggregationRequestDtoMetricCodeEnum.BloodOxygenSaturation]: {
    name: "Blood Oxygen Saturation",
    unit: "%",
    icon: HeartIcon,
    category: "vital",
  },
  [MetricValuesAggregationRequestDtoMetricCodeEnum.HeartRateVariability]: {
    name: "Heart Rate Variability (HRV)",
    unit: "ms",
    icon: HRVIcon,
    category: "vital",
  },
  [MetricValuesAggregationRequestDtoMetricCodeEnum.BloodGlucose]: {
    name: "Blood Glucose",
    unit: "mmol/L",
    icon: BloodIcon,
    category: "vital",
  },
  [MetricValuesAggregationRequestDtoMetricCodeEnum.RespiratoryRate]: {
    name: "Respiratory Rate",
    unit: "breaths per minute",
    icon: AirIcon,
    category: "vital",
  },
  [MetricValuesAggregationRequestDtoMetricCodeEnum.BodyTemperature]: {
    name: "Body Temperature",
    unit: "°F",
    icon: TempIcon,
    category: "vital",
  },

  // Activity Metrics
  [MetricValuesAggregationRequestDtoMetricCodeEnum.StepCounts]: {
    name: "Steps",
    unit: "steps",
    icon: StepsIcon,
    category: "activity",
  },
  [MetricValuesAggregationRequestDtoMetricCodeEnum.Distance]: {
    name: "Distance",
    unit: "miles",
    icon: DistanceIcon,
    category: "activity",
  },
  [MetricValuesAggregationRequestDtoMetricCodeEnum.ActiveCalories]: {
    name: "Active Calories",
    unit: "kcal",
    icon: CaloriesIcon,
    category: "activity",
  },
  [MetricValuesAggregationRequestDtoMetricCodeEnum.Workouts]: {
    name: "Workouts",
    unit: "sessions",
    icon: WorkoutsIcon,
    category: "activity",
  },
  [MetricValuesAggregationRequestDtoMetricCodeEnum.FloorClimbed]: {
    name: "Floors Climbed",
    unit: "floors",
    icon: FloorsIcon,
    category: "activity",
  },

  // Body Composition
  [MetricValuesAggregationRequestDtoMetricCodeEnum.Height]: {
    name: "Height",
    unit: "in",
    icon: HeightIcon,
    category: "body",
  },
  [MetricValuesAggregationRequestDtoMetricCodeEnum.Weight]: {
    name: "Weight",
    unit: "lbs",
    icon: WeightIcon,
    category: "body",
  },
  [MetricValuesAggregationRequestDtoMetricCodeEnum.BodyFatPercentage]: {
    name: "Body Fat Percentage",
    unit: "%",
    icon: WeightIcon,
    category: "body",
  },
  [MetricValuesAggregationRequestDtoMetricCodeEnum.BodyMassIndex]: {
    name: "Body Mass Index",
    unit: "kg/m²",
    icon: WeightIcon,
    category: "body",
  },
  [MetricValuesAggregationRequestDtoMetricCodeEnum.LeanBodyMass]: {
    name: "Lean Body Mass",
    unit: "lb",
    icon: WeightIcon,
    category: "body",
  },
  [MetricValuesAggregationRequestDtoMetricCodeEnum.WaistCircumference]: {
    name: "Waist Circumference",
    unit: "in",
    icon: WeightIcon,
    category: "body",
  },
  [MetricValuesAggregationRequestDtoMetricCodeEnum.HipCircumference]: {
    name: "Hip Circumference",
    unit: "in",
    icon: WeightIcon,
    category: "body",
  },

  // Nutrition
  [MetricValuesAggregationRequestDtoMetricCodeEnum.Calories]: {
    name: "Calories",
    unit: "calorie",
    icon: NutritionIcon,
    category: "nutrition",
  },
  [MetricValuesAggregationRequestDtoMetricCodeEnum.Protein]: {
    name: "Protein",
    unit: "gram",
    icon: NutritionIcon,
    category: "nutrition",
  },
  [MetricValuesAggregationRequestDtoMetricCodeEnum.Carbohydrates]: {
    name: "Carbohydrates",
    unit: "gram",
    icon: NutritionIcon,
    category: "nutrition",
  },
  [MetricValuesAggregationRequestDtoMetricCodeEnum.TotalFat]: {
    name: "Total Fat",
    unit: "gram",
    icon: NutritionIcon,
    category: "nutrition",
  },
  [MetricValuesAggregationRequestDtoMetricCodeEnum.SaturatedFat]: {
    name: "Saturated Fat",
    unit: "gram",
    icon: NutritionIcon,
    category: "nutrition",
  },
  [MetricValuesAggregationRequestDtoMetricCodeEnum.MonounsaturatedFat]: {
    name: "Monounsaturated Fat",
    unit: "gram",
    icon: NutritionIcon,
    category: "nutrition",
  },
  [MetricValuesAggregationRequestDtoMetricCodeEnum.PolyunsaturatedFat]: {
    name: "Polyunsaturated Fat",
    unit: "gram",
    icon: NutritionIcon,
    category: "nutrition",
  },
  [MetricValuesAggregationRequestDtoMetricCodeEnum.Cholesterol]: {
    name: "Cholesterol",
    unit: "milligram per deciliter",
    icon: NutritionIcon,
    category: "nutrition",
  },
  [MetricValuesAggregationRequestDtoMetricCodeEnum.Micronutrients]: {
    name: "Micronutrients",
    unit: "gram",
    icon: NutritionIcon,
    category: "nutrition",
  },
  [MetricValuesAggregationRequestDtoMetricCodeEnum.Hydration]: {
    name: "Hydration",
    unit: "milliliter",
    icon: HydrationIcon,
    category: "nutrition",
  },

  // Sleep
  [MetricValuesAggregationRequestDtoMetricCodeEnum.SleepTime]: {
    name: "Sleep Time",
    unit: "",
    icon: SleepTimeIcon,
    category: "sleep",
  },
  [MetricValuesAggregationRequestDtoMetricCodeEnum.ActualSleepTime]: {
    name: "Actual Sleep Time",
    unit: "",
    icon: ActualSleepTimeIcon,
    category: "sleep",
  },
  [MetricValuesAggregationRequestDtoMetricCodeEnum.TimeInBed]: {
    name: "Time in Bed",
    unit: "",
    icon: BedIcon,
    category: "sleep",
  },
  [MetricValuesAggregationRequestDtoMetricCodeEnum.SleepAnalysis]: {
    name: "Sleep Analysis",
    unit: "",
    icon: SleepAnalysisIcon,
    category: "sleep",
  },
  [MetricValuesAggregationRequestDtoMetricCodeEnum.SleepStages]: {
    name: "Sleep Stages",
    unit: "",
    icon: SleepStagesIcon,
    category: "sleep",
  },
  [MetricValuesAggregationRequestDtoMetricCodeEnum.SleepSessionEvents]: {
    name: "Sleep Session Events",
    unit: "events",
    icon: SleepEventsIcon,
    category: "sleep",
  },
  [MetricValuesAggregationRequestDtoMetricCodeEnum.Menstruation]: {
    name: "Reproductive Health",
    unit: "days",
    icon: FemaleIcon,
    category: "vital",
  },
  [MetricValuesAggregationRequestDtoMetricCodeEnum.WellBeing]: {
    name: "Well Being Score",
    unit: "score",
    icon: WellBeingIcon,
    category: "vital",
  },
};

// Helper function to get metric config by code
export const getMetricConfig = (
  metricCode: string,
): MetricConfig | undefined => {
  return METRIC_CONFIGS[metricCode];
};

// Helper function to get metric config by name (for backward compatibility)
export const getMetricConfigByName = (
  metricName: string,
): MetricConfig | undefined => {
  const config = Object.values(METRIC_CONFIGS).find(
    (config) => config.name.toLowerCase() === metricName.toLowerCase(),
  );
  return config;
};

// Get all metrics by category
export const getMetricsByCategory = (category: MetricConfig["category"]) => {
  return Object.entries(METRIC_CONFIGS)
    .filter(([, config]) => config.category === category)
    .reduce(
      (acc, [code, config]) => {
        acc[code] = config;
        return acc;
      },
      {} as Record<string, MetricConfig>,
    );
};
